import React from 'react';
import {
  TrendingUp,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Activity,
  Loader2
} from 'lucide-react';
import { motion } from 'framer-motion';

const WithdrawalStats = ({ stats, isLoading, onRefresh }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading statistics...</span>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 mb-4">Failed to load withdrawal statistics</p>
        <button
          onClick={onRefresh}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const formatAmount = (amount, currency = 'PI') => {
    const symbol = currency === 'NGN' ? '₦' : 'π';
    if (!amount) return `${symbol} 0`;
    const numAmount = parseFloat(amount);

    if (numAmount >= 1000000) {
      return `${symbol} ${(numAmount / 1000000).toFixed(1)}M`;
    } else if (numAmount >= 1000) {
      return `${symbol} ${(numAmount / 1000).toFixed(1)}K`;
    } else {
      return `${symbol} ${numAmount.toLocaleString()}`;
    }
  };

  const mainStats = [
    {
      label: 'Total Withdrawals',
      value: stats.total || 0,
      icon: TrendingUp,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
    {
      label: 'Total Amount Withdrawn',
      value: formatAmount(stats.totalAmount || 0), // Default to PI for aggregate stats usually, or handle mixed? safely defaults to PI
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    {
      label: 'Pending Approvals',
      value: stats.pending || 0,
      icon: Clock,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      borderColor: 'border-yellow-200'
    },
    {
      label: 'Completed',
      value: stats.completed || 0,
      icon: CheckCircle2,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    }
  ];


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      {/* Main Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {mainStats.map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className={`bg-white border ${stat.borderColor} rounded-lg p-4 hover:shadow-md transition-shadow`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <div className={`p-3 ${stat.bgColor} rounded-lg`}>
                <stat.icon className={`w-6 h-6 ${stat.color}`} />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Status Breakdown */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Status Breakdown</h3>
          <button
            onClick={onRefresh}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
            <Clock className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{stats.pending || 0}</p>
            <p className="text-sm text-gray-600">Pending</p>
          </div>

          <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
            <RefreshCw className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{stats.processing || 0}</p>
            <p className="text-sm text-gray-600">Processing</p>
          </div>

          <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
            <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{stats.completed || 0}</p>
            <p className="text-sm text-gray-600">Completed</p>
          </div>

          <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
            <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-gray-900">{stats.rejected || 0}</p>
            <p className="text-sm text-gray-600">Rejected</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {stats.recentActivity && stats.recentActivity.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Withdrawal Activity</h3>

          <div className="space-y-3">
            {stats.recentActivity.slice(0, 10).map((activity, index) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <Activity className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {formatAmount(activity.requested_amount)} withdrawal
                    </p>
                    <p className="text-sm text-gray-600">
                      {activity.project?.title || 'Unknown Project'} • {activity.owner?.username || 'Unknown Owner'}
                    </p>
                  </div>
                </div>

                <div className="text-right">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${formatStatus(activity.status).bgClass
                    }`}>
                    {formatStatus(activity.status).label}
                  </span>
                  <p className="text-xs text-gray-500 mt-1">
                    {formatDate(activity.requested_at)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Processing Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-2">Withdrawal Processing Information:</p>
            <ul className="space-y-1 text-xs">
              <li>• Withdrawals ≤10,000 π are automatically approved and processed</li>
              <li>• Withdrawals {'>'}10,000 π require manual admin approval</li>
              <li>• Processing typically takes 5-15 minutes after approval</li>
              <li>• Only one withdrawal can be processed at a time due to blockchain limitations</li>
              <li>• All withdrawals are logged in the activity trail for audit purposes</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  function formatStatus(status) {
    const statusMap = {
      pending: {
        label: 'Pending',
        bgClass: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      },
      approved: {
        label: 'Approved',
        bgClass: 'bg-blue-100 text-blue-800 border-blue-200'
      },
      processing: {
        label: 'Processing',
        bgClass: 'bg-blue-100 text-blue-800 border-blue-200'
      },
      completed: {
        label: 'Completed',
        bgClass: 'bg-green-100 text-green-800 border-green-200'
      },
      rejected: {
        label: 'Rejected',
        bgClass: 'bg-red-100 text-red-800 border-red-200'
      },
      failed: {
        label: 'Failed',
        bgClass: 'bg-red-100 text-red-800 border-red-200'
      }
    };

    return statusMap[status] || {
      label: status,
      bgClass: 'bg-gray-100 text-gray-800 border-gray-200'
    };
  }
};

export default WithdrawalStats;
